/**
 * Reconnecting WebSocket to `/ws/events`. Dispatches into the console store.
 */

import type { WsEvent } from "./types";

export type WsHandler = (event: WsEvent) => void;

const INITIAL_DELAY_MS = 600;
const MAX_DELAY_MS = 12_000;

function wsUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws/events`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") return null;
  return value as Record<string, unknown>;
}

/**
 * Normalize backend event envelopes into {@link WsEvent}.
 */
export function normalizeEvent(raw: unknown): WsEvent | null {
  const record = asRecord(raw);
  if (!record) return null;
  const nested = asRecord(record.payload) ?? asRecord(record.data);
  const typeRaw = record.type ?? record.event ?? record.kind;
  if (typeof typeRaw !== "string") return null;
  const merged: Record<string, unknown> = { ...nested, ...record, type: typeRaw };
  return merged as unknown as WsEvent;
}

/**
 * Open a reconnecting socket. Returns a disposer.
 */
export function connectEvents(onEvent: WsHandler, onStatus?: (open: boolean) => void): () => void {
  let socket: WebSocket | null = null;
  let disposed = false;
  let delay = INITIAL_DELAY_MS;
  let timer: number | null = null;

  const clearTimer = () => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  const open = () => {
    if (disposed) return;
    clearTimer();
    try {
      socket = new WebSocket(wsUrl());
    } catch {
      schedule();
      return;
    }
    socket.addEventListener("open", () => {
      delay = INITIAL_DELAY_MS;
      onStatus?.(true);
    });
    socket.addEventListener("message", (message) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(String(message.data));
      } catch {
        return;
      }
      const event = normalizeEvent(parsed);
      if (event) onEvent(event);
    });
    socket.addEventListener("close", () => {
      onStatus?.(false);
      if (!disposed) schedule();
    });
    socket.addEventListener("error", () => {
      socket?.close();
    });
  };

  const schedule = () => {
    if (disposed) return;
    clearTimer();
    timer = window.setTimeout(open, delay);
    delay = Math.min(MAX_DELAY_MS, Math.round(delay * 1.7));
  };

  open();

  return () => {
    disposed = true;
    clearTimer();
    onStatus?.(false);
    if (socket) {
      socket.close();
      socket = null;
    }
  };
}
